import { useState, useCallback } from 'react';

const TOOLTIP_STORAGE_KEY = 'vibary_tooltips_seen';

/**
 * Hook to manage contextual tooltip seen state.
 * Tooltips show once per feature, stored in localStorage.
 *
 * @param {string} tooltipId - Unique identifier for the tooltip
 * @returns {{ hasSeen: boolean, markSeen: () => void, reset: () => void }}
 */
export function useTooltipSeen(tooltipId) {
  // DISABLED: All contextual tooltips are currently disabled
  // To re-enable, restore the original useState logic below
  const [hasSeen, setHasSeen] = useState(true);

  const markSeen = useCallback(() => {
    try {
      const stored = localStorage.getItem(TOOLTIP_STORAGE_KEY);
      const seenTooltips = stored ? JSON.parse(stored) : [];
      if (!seenTooltips.includes(tooltipId)) {
        seenTooltips.push(tooltipId);
        localStorage.setItem(TOOLTIP_STORAGE_KEY, JSON.stringify(seenTooltips));
      }
      setHasSeen(true);
    } catch {
      setHasSeen(true);
    }
  }, [tooltipId]);

  const reset = useCallback(() => {
    try {
      const stored = localStorage.getItem(TOOLTIP_STORAGE_KEY);
      if (stored) {
        const seenTooltips = JSON.parse(stored);
        const filtered = seenTooltips.filter(id => id !== tooltipId);
        localStorage.setItem(TOOLTIP_STORAGE_KEY, JSON.stringify(filtered));
      }
      setHasSeen(false);
    } catch {
      setHasSeen(false);
    }
  }, [tooltipId]);

  return { hasSeen, markSeen, reset };
}

/**
 * Reset all tooltip seen states (useful for testing)
 */
export function resetAllTooltips() {
  localStorage.removeItem(TOOLTIP_STORAGE_KEY);
}

/**
 * Get all seen tooltip IDs
 * @returns {string[]}
 */
export function getSeenTooltips() {
  try {
    const stored = localStorage.getItem(TOOLTIP_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export default useTooltipSeen;
